<template>
  <!-- Sin `scrollable`: es lo que le pone overflow:auto a la caja del modal y
       recortaba el globo de la (i). El contenido son tres campos, no necesita scroll. -->
  <modal :name="nombreModal" :adaptive="true" height="auto" width="520px">
    <div class="alta">
      <div class="modal-header alta-cabecera">
        <h4>{{ editando ? $t("editarEtiqueta") : $t("nuevaEtiqueta") }}</h4>
      </div>

      <div class="modal-body alta-cuerpo">
        <div class="alta-campo">
          <label for="altaNombre">{{ $t("nombre") }}</label>
          <input
            id="altaNombre"
            ref="altaNombre"
            type="text"
            class="form-control"
            maxlength="125"
            v-model="alta.name"
            :placeholder="$t('nombreEtiqueta')"
            @keyup.enter="confirmar()"
          />
        </div>

        <div class="alta-campo">
          <label>
            {{ $t("familia") }}
            <!-- La (i) explica para que sirve cada familia: es la decision que no se
                 puede deshacer desde aqui, y la que hoy se toma mal. -->
            <span class="info" tabindex="0" :aria-label="$t('queEsCadaFamilia')">
              i
              <span class="info-globo" role="tooltip">
                <b>{{ $t("materiaPrima") }}</b> — {{ $t("ayudaFamiliaMateriaPrima") }}<br /><br />
                <b>{{ $t("proveedor") }}</b> — {{ $t("ayudaFamiliaProveedor") }}
              </span>
            </span>
          </label>

          <!-- Con familia fija -desde la ficha de materia prima solo cabe 'A'- se ensena
               cual es y por que, en vez de un boton que no se puede pulsar. -->
          <p class="alta-fija" v-if="tipoBloqueado">
            {{ alta.type === "P" ? $t("proveedor") : $t("materiaPrima") }} ·
            {{ alta.type === "P" ? $t("ayudaFamiliaProveedor") : $t("ayudaFamiliaMateriaPrima") }}
          </p>
          <p class="alta-nota" v-if="editando">{{ $t("laFamiliaNoSeCambia") }}</p>
          <div class="alta-familias" v-else>
            <button
              type="button"
              :aria-pressed="alta.type === 'A' ? 'true' : 'false'"
              @click="elegirFamilia('A')"
            >
              {{ $t("materiaPrima") }}
            </button>
            <button
              type="button"
              :aria-pressed="alta.type === 'P' ? 'true' : 'false'"
              @click="elegirFamilia('P')"
            >
              {{ $t("proveedor") }}
            </button>
          </div>
        </div>

        <div class="alta-campo">
          <label for="altaPadre">{{ $t("etiquetaPadre") }}</label>
          <select id="altaPadre" class="form-control" v-model="alta.parentCode">
            <option :value="0">{{ $t("ningunaEsPrincipal") }}</option>
            <option v-for="padre in posiblesPadres" :key="'p' + padre.id" :value="padre.code">
              {{ padre.ruta }}
            </option>
          </select>
          <p class="alta-nota">{{ $t("ayudaEtiquetaPadre") }}</p>
        </div>

        <p class="alta-error" v-if="error">{{ error }}</p>
      </div>

      <div class="modal-footer alta-pie">
        <button type="button" class="btn btn-claro" :disabled="ocupado" @click="cerrar()">
          {{ $t("cancelar") }}
        </button>
        <button type="button" class="btn btn-verde" :disabled="ocupado" @click="confirmar()">
          {{ editando ? $t("guardar") : $t("crear") }}
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
/**
 * Alta de etiqueta, en modal.
 *
 * La usan la pantalla de etiquetas y la ficha de materia prima. Se hizo componente al
 * necesitarla el segundo sitio: dos copias del mismo formulario acaban divergiendo, y lo
 * que se explica en la (i) es justo lo que no puede decirse de dos maneras distintas.
 *
 * El componente NO escribe en la base de datos. Emite `confirmar` con {name, type,
 * parentCode} y quien lo usa decide:
 *
 *  - La pantalla de etiquetas la crea al momento, porque es lo unico que se hace alli.
 *  - La ficha de materia prima se la guarda y la crea al guardar la ficha, para no dejar
 *    etiquetas sueltas si el usuario se arrepiente. Misma norma que sigue sectorizacion
 *    con las materias primas de un producto.
 */
export default {
  name: "EtiquetaNuevaModal",

  props: {
    nombreModal: { type: String, default: "modalNuevaEtiqueta" },
    idBusiness: { type: [String, Number], required: true },
    // 'A' o 'P' para dejarla fija; vacio para que el usuario elija.
    familiaFija: { type: String, default: "" },
    ocupado: { type: Boolean, default: false }
  },

  data() {
    return {
      etiquetas: [],
      error: "",
      editando: false,
      alta: { id: 0, name: "", type: "A", parentCode: 0 }
    };
  },

  computed: {
    tipoBloqueado() {
      return this.editando || !!this.familiaFija;
    },

    /**
     * Padres posibles: de la MISMA familia y activos. Es la mitad de la regla -la otra
     * mitad la vuelve a comprobar la API-, y evita crear de entrada una etiqueta colgada
     * de la otra familia, que es como se llega a las que no se ven en el panel.
     *
     * Al editar se quitan ademas la propia etiqueta y sus descendientes: colgarla de una
     * de sus hijas cerraria la rama sobre si misma y desapareceria del panel. La API lo
     * vuelve a comprobar; aqui simplemente no se ofrece.
     */
    posiblesPadres() {
      let vm = this;
      let prohibidos = this.editando ? this.ramaDe(this.alta.id) : {};

      return this.etiquetas
        .filter(t => t.tipo === vm.alta.type && !t.baja && !prohibidos[t.id])
        .slice()
        .sort((a, b) => (a.ruta || "").localeCompare(b.ruta || ""));
    }
  },

  methods: {
    /**
     * @param {string} tipoSugerido Familia con la que abrir, si no viene fija.
     */
    abrir(tipoSugerido) {
      this.error = "";
      this.editando = false;
      this.alta = {
        id: 0,
        name: "",
        type: this.familiaFija || tipoSugerido || "A",
        parentCode: 0
      };

      this.cargarEtiquetas();
      this.$modal.show(this.nombreModal);

      let vm = this;
      this.$nextTick(function() {
        if (vm.$refs.altaNombre) {
          vm.$refs.altaNombre.focus();
        }
      });
    },

    /**
     * Abre el mismo modal con los datos puestos. El tipo se ensena pero no se cambia.
     *
     * @param {object} tag Fila tal como la devuelve getBusinessTags.
     */
    abrirEdicion(tag) {
      this.error = "";
      this.editando = true;
      this.alta = {
        id: tag.id,
        name: tag.nombre,
        type: tag.tipo,
        parentCode: tag.parentCode
      };

      this.cargarEtiquetas();
      this.$modal.show(this.nombreModal);

      let vm = this;
      this.$nextTick(function() {
        if (vm.$refs.altaNombre) {
          vm.$refs.altaNombre.focus();
        }
      });
    },

    /**
     * La etiqueta dada y todo lo que cuelga de ella, como {id: true}.
     *
     * Se recorre hacia abajo por `parentCode` -> `code`, con un tope de vueltas por si la
     * rama ya trae un bucle de las que hay en produccion.
     */
    ramaDe(id) {
      let dentro = {};
      let porId = {};
      let hijosDe = {};

      this.etiquetas.forEach(function(t) {
        porId[t.id] = t;
        hijosDe[t.parentCode] = hijosDe[t.parentCode] || [];
        hijosDe[t.parentCode].push(t);
      });

      if (!porId[id]) {
        return dentro;
      }

      let pila = [porId[id]];
      let vueltas = 0;

      while (pila.length && vueltas < 500) {
        let actual = pila.pop();
        vueltas++;

        if (dentro[actual.id]) {
          continue;
        }

        dentro[actual.id] = true;
        (hijosDe[actual.code] || []).forEach(function(hijo) {
          pila.push(hijo);
        });
      }

      return dentro;
    },

    cerrar() {
      this.$modal.hide(this.nombreModal);
    },

    mostrarError(mensaje) {
      this.error = mensaje;
    },

    cargarEtiquetas() {
      var config = {
        params: { idBusiness: this.idBusiness, jwt: this.$store.getters.jwt },
        headers: { Authorization: "Bearer " + this.$store.getters.jwt }
      };

      let vm = this;
      this.axios
        .get(this.$store.getters.apiUrl + "categories/getBusinessTags", config)
        .then(function(response) {
          if (response.data.response) {
            vm.etiquetas = response.data.result.etiquetas;
          }
        })
        .catch(function(error) {
          // Sin lista de padres la etiqueta se crea igual, como principal.
          console.log(error);
        });
    },

    elegirFamilia(tipo) {
      if (this.alta.type === tipo) {
        return;
      }

      // El padre pertenece a una familia: al cambiarla deja de valer.
      this.alta.type = tipo;
      this.alta.parentCode = 0;
    },

    confirmar() {
      if (this.ocupado) {
        return;
      }

      if (!this.alta.name.trim()) {
        this.error = this.$t("nombreEtiquetaObligatorio");
        return;
      }

      this.error = "";
      this.$emit("confirmar", {
        id: this.alta.id,
        name: this.alta.name.trim(),
        type: this.alta.type,
        parentCode: this.alta.parentCode,
        editando: this.editando
      });
    }
  }
};
</script>

<style scoped>
/* ---------------------------------------------------------------------------
   Alta de etiqueta

   En modal y no en pantalla aparte: son tres campos y el usuario esta mirando
   aquello donde va a usar lo que cree. Sacarle de alli para pedirle un nombre le
   hace perder de vista -y en la ficha de materia prima, perder de verdad- lo que
   tenia delante.
   --------------------------------------------------------------------------- */
.alta-cabecera {
    padding: 16px 20px;
    background: #f7f9fb;
    border-bottom: 1px solid #e6e9ef;
}

.alta-cabecera h4 {
    margin: 0;
    font-size: 1.05em;
    font-weight: 600;
    color: #2c3e50;
}

.alta-cuerpo {
    padding: 18px 20px;
}

.alta-campo {
    margin-bottom: 16px;
}

.alta-campo:last-child {
    margin-bottom: 0;
}

.alta-campo > label {
    display: block;
    font-size: 0.74em;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #8b96a3;
    font-weight: 600;
    margin-bottom: 6px;
}

/* Las dos familias, como las pestanas de la pantalla de etiquetas: la puesta en
   turquesa. Son dos opciones excluyentes y se ven las dos a la vez, que es lo que
   hace falta cuando la eleccion es justo la que se esta explicando. */
.alta-familias {
    display: flex;
    gap: 8px;
}

.alta-familias button {
    flex: 1 1 0;
    border: 1px solid #dde2e9;
    background: #fff;
    border-radius: 3px;
    padding: 8px 12px;
    font-size: 0.86em;
    color: #4a5765;
}

.alta-familias button:hover {
    border-color: #009982;
    color: #009982;
}

.alta-familias button[aria-pressed="true"] {
    background: #009982;
    border-color: #009982;
    color: #fff;
    font-weight: 600;
}

.alta-fija {
    margin: 0;
    padding: 8px 12px;
    background: #f7f9fb;
    border: 1px solid #e6e9ef;
    border-radius: 3px;
    font-size: 0.84em;
    color: #4a5765;
}

/* ---- La (i) ---- */
.info {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 15px;
    height: 15px;
    margin-left: 4px;
    border: 1px solid #c3cbd5;
    border-radius: 50%;
    font-size: 10px;
    font-weight: 700;
    font-style: italic;
    text-transform: none;
    color: #8b96a3;
    cursor: help;
    position: relative;
}

.info:hover,
.info:focus {
    border-color: #009982;
    color: #009982;
    outline: none;
}

/* Debajo y pegado a la izquierda del icono, no centrado encima.

   Encima se salia por el borde superior del modal, y centrado sobre un icono de
   15px que esta casi en el margen izquierdo se salia tambien por ese lado. Debajo
   hay sitio de sobra: el campo de la familia es el segundo de tres. */
.info-globo {
    display: none;
    position: absolute;
    left: -6px;
    top: calc(100% + 8px);
    width: 300px;
    max-width: calc(100vw - 60px);
    padding: 10px 12px;
    background: #2c3e50;
    color: #fff;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 400;
    font-style: normal;
    line-height: 1.45;
    text-transform: none;
    letter-spacing: 0;
    text-align: left;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
    z-index: 10;
}

/* Tambien con :focus, para quien no usa raton. */
.info:hover .info-globo,
.info:focus .info-globo {
    display: block;
}

.info-globo::after {
    content: "";
    position: absolute;
    bottom: 100%;
    left: 11px;
    border: 5px solid transparent;
    border-bottom-color: #2c3e50;
}

.alta-nota {
    margin: 6px 0 0;
    font-size: 0.8em;
    color: #98a2ad;
}

.alta-error {
    margin: 14px 0 0;
    padding: 9px 12px;
    border-radius: 4px;
    background: rgba(161, 58, 47, 0.1);
    border: 1px solid rgba(161, 58, 47, 0.22);
    color: #a13a2f;
    font-size: 0.85em;
}

.alta-pie {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 14px 20px;
    background: #f7f9fb;
    border-top: 1px solid #e6e9ef;
}

.btn-verde {
    background: #009982;
    border: 1px solid #009982;
    color: #fff;
    border-radius: 3px;
    padding: 8px 16px;
    font-size: 0.88em;
    font-weight: 600;
}

.btn-verde:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}

.btn-claro {
    background: #fff;
    border: 1px solid #dde2e9;
    color: #4a5765;
    border-radius: 3px;
    padding: 8px 16px;
    font-size: 0.88em;
}

.btn-claro:hover:not(:disabled) {
    border-color: #009982;
    color: #009982;
}

.btn-claro:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
</style>



// WEBPACK FOOTER //
// src/pages/Widgets/Etiquetas/EtiquetaNuevaModal.vue